import React, { useEffect, useMemo, useState } from "react";
import { Check, Loader2, ShieldCheck } from "lucide-react";

import { getVisualDirectionGenerationContract } from "../../lib/brandStudioApi";

const GROUP_LABELS = {
  color: "Цвет",
  typography: "Типографика",
  materiality: "Материалы",
  photography: "Фотография",
  graphic_language: "Графический язык",
  composition: "Композиция",
  applications: "Носители",
};

export default function VisualDirectionGenerationContractOwnerPanelV1() {
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setStatus("loading");
    setError("");
    try {
      setData(await getVisualDirectionGenerationContract());
      setStatus("ready");
    } catch (problem) {
      const detail = problem?.response?.data?.detail;
      const code = typeof detail === "object" && detail ? detail.code : typeof detail === "string" ? detail : "";
      setError(code || "Не удалось собрать VD2 generation contract.");
      setStatus("error");
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const item of data?.selected_patterns || []) {
      if (!map[item.group]) map[item.group] = [];
      map[item.group].push(item);
    }
    return map;
  }, [data]);

  return (
    <section className="rounded-[24px] border border-dashed border-[#7A684F] bg-[#FFF9F0] p-5 md:p-7" data-testid="visual-direction-generation-contract-owner-panel-v1">
      <div className="text-[9.5px] uppercase tracking-[0.2em] text-[#8A6D53]">OWNER QA · VD2</div>
      <h3 className="mt-2 font-serif text-[27px] leading-tight text-[#2B211A]">Generation Contract + Evidence Selector</h3>
      <p className="mt-2 max-w-3xl text-[12.5px] leading-5 text-[#655141]">
        Read-only контракт для будущей генерации Visual Direction. Он выбирает bounded evidence детерминированно,
        не вызывает Mistral/Prodia/OpenAI и не принимает финальных brand decisions.
      </p>

      {status === "loading" && (
        <div className="mt-5 inline-flex items-center gap-2 text-[12px] text-[#796652]">
          <Loader2 size={14} className="animate-spin" /> Собираю generation contract из VD1…
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 rounded-[13px] border border-[#E2CFC8] bg-[#FFF4EF] px-4 py-3 text-[12px] leading-5 text-[#6B443A]">{error}</div>
      )}

      {status === "ready" && data && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EEF0DF] px-3 py-1.5 text-[10.5px] text-[#586044]">
              {data.ready_for_provider_adapter ? <Check size={12} /> : <ShieldCheck size={12} />}
              {data.ready_for_provider_adapter ? "Готово к provider adapter" : "Generation Contract пока не готов"}
            </span>
            <span className="text-[10.5px] text-[#8A745E]">{data.contract_version} · {data.direction_kind}</span>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Semantic evidence</div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                total: {data.selection_report.semantic_pattern_total}<br />
                selected: {data.selection_report.semantic_pattern_selected}<br />
                strong: {data.selection_report.strong_pattern_selected}<br />
                user-backed weak: {data.selection_report.user_backed_weak_pattern_selected}
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Reference universe</div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                universe: {data.selection_report.reference_universe_total}<br />
                exemplars: {data.selection_report.reference_exemplar_selected}<br />
                upstream preserved: yes
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Coverage</div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                groups: {(data.selection_report.selected_groups || []).length}<br />
                patterns with exemplar: {data.selection_report.selected_patterns_with_reference_exemplar}<br />
                without exemplar: {data.selection_report.selected_patterns_without_reference_exemplar}
              </div>
            </article>

            <article className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Output policy</div>
              <div className="mt-2 text-[12px] leading-5 text-[#4C392B]">
                requested: {data.requested_output_count ?? "user decides later"}<br />
                policy: {data.selection_policy.output_count_policy}<br />
                mandatory variants: none
              </div>
            </article>
          </div>

          {(data.explicit_decisions || []).length > 0 && (
            <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-white p-4">
              <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Source Truth · always above visual frequency</div>
              <div className="mt-2 space-y-2">
                {data.explicit_decisions.map((item) => (
                  <div key={item.decision_id} className="text-[11.5px] leading-5 text-[#4C392B]">• {item.value}</div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(grouped).map(([group, items]) => (
              <article key={group} className="rounded-[14px] border border-[#DED0BC] bg-white p-4">
                <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">{GROUP_LABELS[group] || group}</div>
                <div className="mt-2 space-y-2">
                  {items.map((item) => (
                    <div key={item.pattern_id}>
                      <div className="text-[11.5px] leading-5 text-[#4C392B]">• {item.label}</div>
                      <div className="text-[9.5px] text-[#8A745E]">{item.strength} · refs {item.reference_ids?.length || 0} · {item.selection_reason}</div>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>

          <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-[#FFFCF6] p-4">
            <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Selected reference exemplars</div>
            <div className="mt-2 space-y-1.5 text-[11px] leading-5 text-[#655141]">
              {(data.selected_references || []).map((item) => (
                <div key={item.reference_id}>
                  #{item.order + 1} · supports {item.supporting_pattern_ids?.length || 0} selected patterns
                  {item.note ? " · user note" : ""}
                  {item.signals?.length ? " · " + item.signals.length + " user signals" : ""}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 rounded-[14px] border border-[#DED0BC] bg-[#FFFCF6] p-4">
            <div className="text-[9.5px] uppercase tracking-[0.15em] text-[#927A5B]">Generation rules</div>
            <div className="mt-2 text-[11px] leading-5 text-[#655141]">
              Source Truth overrides visual frequency · semantic evidence is advisory · unselected evidence cannot silently become a brand rule ·
              exact name/logo/font and production files stay with Controlled Renderer · provider/model deferred.
            </div>
          </div>

          <div className="mt-4 text-[10.5px] leading-5 text-[#8A745E]">
            Internal budgets ({data.selection_policy.semantic_pattern_input_budget} patterns / {data.selection_policy.reference_exemplar_input_budget} exemplars)
            bound provider input only. They are not customer output limits.
          </div>
        </div>
      )}
    </section>
  );
}
